import React, { useState } from "react";
import { experience } from "../data/mock";
import { Calendar, MapPin, ChevronDown, ChevronUp } from "lucide-react";

const Experience = () => {
  const [expandedId, setExpandedId] = useState(null);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="text-center mb-16">
        <h2 className="text-5xl lg:text-6xl font-light tracking-tight text-black mb-6">
          Professional Experience
        </h2>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto font-light leading-relaxed">
          A track record of driving digital transformation and innovation across diverse industries, 
          from strategic leadership to hands-on technical implementation.
        </p>
      </div>

      <div className="max-w-4xl mx-auto">
        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-8 top-0 bottom-0 w-px bg-gray-200 hidden md:block" />

          {experience.map((exp, index) => (
            <div key={exp.id} className="relative mb-12 md:mb-16">
              {/* Timeline dot */}
              <div className="absolute left-6 w-4 h-4 bg-black rounded-full border-4 border-white shadow-sm hidden md:block" />

              {/* Content */}
              <div className="md:ml-20">
                <div 
                  className="bg-white border border-gray-200 rounded-lg p-8 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                  onClick={() => toggleExpand(exp.id)}
                >
                  {/* Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl lg:text-2xl font-light text-black mb-2 tracking-wide">
                        {exp.position}
                      </h3>
                      <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 text-gray-600">
                        <div className="flex items-center space-x-2">
                          <MapPin size={16} />
                          <span className="font-light">{exp.company}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Calendar size={16} />
                          <span className="font-light">{exp.period}</span>
                        </div>
                      </div>
                    </div>
                    <button className="p-2 hover:bg-gray-50 rounded-lg transition-colors">
                      {expandedId === exp.id ? 
                        <ChevronUp size={20} className="text-gray-400" /> : 
                        <ChevronDown size={20} className="text-gray-400" />
                      }
                    </button>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 font-light leading-relaxed mb-4">
                    {exp.description}
                  </p>

                  {/* Achievements - Expandable */}
                  {expandedId === exp.id && (
                    <div className="border-t border-gray-100 pt-6 mt-6 animate-fade-in">
                      <h4 className="text-lg font-light text-black mb-4 tracking-wide">
                        Key Achievements
                      </h4>
                      <ul className="space-y-3">
                        {exp.achievements.map((achievement, achievementIndex) => (
                          <li 
                            key={achievementIndex}
                            className="flex items-start space-x-3 text-gray-600 font-light leading-relaxed"
                          >
                            <div className="w-2 h-2 bg-black rounded-full mt-2 flex-shrink-0" />
                            <span>{achievement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Expand hint */}
                  {expandedId !== exp.id && (
                    <div className="text-sm text-gray-400 font-light mt-4">
                      Click to view key achievements →
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-16 border-t border-gray-200">
          <div className="text-center">
            <div className="text-3xl font-light text-black mb-2">10+</div>
            <div className="text-sm text-gray-600 font-light tracking-wide">Years Experience</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-light text-black mb-2">15+</div>
            <div className="text-sm text-gray-600 font-light tracking-wide">Projects Led</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-light text-black mb-2">5+</div>
            <div className="text-sm text-gray-600 font-light tracking-wide">Companies Transformed</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-light text-black mb-2">100%</div>
            <div className="text-sm text-gray-600 font-light tracking-wide">Success Rate</div>
          </div>
        </div>
      </div>

      {/* Custom animation styles */}
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </section>
  );
};

export default Experience;